import moment from 'moment';
import DateRangeFilter from 'components/Grid/Filter/DateRange';
import BooleanView from 'components/DataGrid/ValueViews/BooleanView';
import OrderStatusCell from 'components/Grid/Cells/OrderStatusCell';
import Addons from 'views/EcommerceBags/components/CurrentAddons';
import { translateOrderStatuses } from 'views/Orders/Orders/statusTranslations';
import DeliveryPhoto from 'components/DeliveryPhoto/DeliveryPhoto';

import EcommerceDateRange from '../components/EcommerceDateRange';
import YesNoDataGridFilter from '../components/YesNoDataGridFilter';
import PriceAfterDiscountTooltip from '../components/PriceAfterDiscountTooltip';

const columnConfig = t => [
  {
    title: 'ecommerceBags.fields.id',
    name: 'id',
    accessor: 'id',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.id',
    name: 'clientDiet.id',
    width: 70,
    filterable: true,
    sortable: true,
    accessor: row => (
      <a
        target="_blank"
        rel="noreferrer"
        href={`/admin/ecommerce-diets/preview/${row.clientDiet.id}`}
      >
        {row.clientDiet.id}
      </a>
    ),
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.order.id',
    accessor: 'clientDiet.order.id',
    name: 'clientDiet.order.id',
    width: 70,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.isSubscription',
    accessor: row => <BooleanView value={row.clientDiet.isSubscription} />,
    name: 'clientDiet.isSubscription',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.active',
    accessor: row => <BooleanView value={row.clientDiet.active} />,
    name: 'clientDiet.active',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.order.status',
    accessor: row => (
      <OrderStatusCell
        status={{
          value: row.clientDiet.order.status.value,
          systemValue: row.clientDiet.order.status.systemValue,
        }}
      />
    ),
    name: 'clientDiet.order.status.systemValue',
    width: 120,
    Filter: ({ onChange, filter }) => {
      const selectOptions = translateOrderStatuses(t);

      return (
        <select
          style={{ height: '30px', width: '100%', padding: '0' }}
          onChange={e => onChange(e.target.value)}
          value={filter?.value || ''}
        >
          <option value="">{selectOptions[0].label}</option>
          <option value={selectOptions[1].systemValue}>
            {selectOptions[1].label}
          </option>
          <option value={selectOptions[2].systemValue}>
            {selectOptions[2].label}
          </option>
          <option value={selectOptions[3].systemValue}>
            {selectOptions[3].label}
          </option>
        </select>
      );
    },
    filterable: true,
    sortable: false,
  },

  {
    title: 'ecommerceBags.fields.bag.date',
    accessor: row => (row.date ? moment(row.date).format('YYYY-MM-DD') : null),
    name: 'date',
    Filter: ({ onChange, filter }) => {
      const validationCalendar = current => {
        const minDate = moment().subtract(13, 'days').startOf('day');
        return current.isSameOrAfter(minDate, 'day');
      };

      return (
        <EcommerceDateRange
          onChange={onChange}
          value={filter?.value}
          validationCalendar={validationCalendar}
        />
      );
    },
    width: 90,
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.deliveryTime',
    accessor: row =>
      row.deliveredAt ? moment(row.deliveredAt).format('llll') : null,
    name: 'deliveredAt',
    Filter: ({ onChange, filter }) => (
      <DateRangeFilter onChange={onChange} value={filter?.value} />
    ),
    filterable: true,
    sortable: false,
  },
  {
    title: t('ecommerceBags.fields.bag.deliveryPhoto'),
    accessor: row =>
      row.deliveryImage ? (
        <DeliveryPhoto src={row.deliveryImage} id={row.id} />
      ) : null,
    name: 'deliveryPhoto',
    filterable: false,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.client',
    accessor: row => {
      const { id } = row.clientDiet.client;
      const first = row.clientDiet.client.firstName || '';
      const last = row.clientDiet.client.lastName || '';
      if (!id) {
        return '-';
      }

      return (
        <a target="_blank" rel="noreferrer" href={`/admin/clients/edit/${id}`}>
          {first} {last}
        </a>
      );
    },
    name: 'user',
    width: 200,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.client.email',
    accessor: row => {
      const { email } = row.clientDiet.client;
      return <a href={`mailto:${email}`}>{email}</a>;
    },
    width: 200,
    name: 'clientDiet.client.email',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.addons',
    accessor: row => <Addons addons={row?.addons} />,
    width: 200,
    name: 'hasAddons',
    filterable: false,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.client.phone.number',
    accessor: row => {
      const { number } = row.clientDiet.client.phone;
      return <a href={`tel:${number}`}>{number}</a>;
    },
    width: 130,
    name: 'clientDiet.client.phone.number',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.address.city',
    accessor: 'address.city',
    name: 'address.city',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.address',
    accessor: row => {
      const city = row?.address?.city;
      const postCode = row?.address?.postCode || '-';
      const street = row?.address?.street || '-';
      const buildNumber = row?.address?.buildNumber || '-';

      const address = city
        ? `${city} ${postCode}, ${street} ${buildNumber}`
        : null;

      return address || row?.pickUpPoint?.value || '-';
    },
    width: 240,
    name: 'address',
    sortable: true,
    filterable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.address.zone.name',
    accessor: 'address.zone.name',
    name: 'address.zone.name',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.realFoodCost',
    accessor: 'realFoodCost',
    name: 'realFoodCost',
    width: 70,
    filterable: false,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.priceFromMoneyBox',
    accessor: row => row.clientDiet.order.priceFromMoneyBox || '0.00',
    name: 'clientDiet.order.priceFromMoneyBox',
    width: 70,
    filterable: false,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.priceAfterDiscount',
    name: 'priceAfterDiscountTotal',
    accessor: ({
      ecoContainerPrice,
      priceAddonsAfterDiscount,
      priceAddonsBeforeDiscount,
      priceAddonsDiscount,
      priceAfterDiscount,
      priceBeforeDiscount,
      priceDeliveryAfterDiscount,
      priceDeliveryBeforeDiscount,
      priceDeliveryDiscount,
      priceDiscount,
      priceDishesAfterDiscount,
      priceDishesBeforeDiscount,
      priceDishesDiscount,
    }) => (
      <div style={{ textAlign: 'right', paddingRight: 8 }}>
        <PriceAfterDiscountTooltip
          ecoContainerPrice={ecoContainerPrice}
          priceAddonsAfterDiscount={priceAddonsAfterDiscount}
          priceAddonsBeforeDiscount={priceAddonsBeforeDiscount}
          priceAddonsDiscount={priceAddonsDiscount}
          priceAfterDiscount={priceAfterDiscount}
          priceBeforeDiscount={priceBeforeDiscount}
          priceDeliveryAfterDiscount={priceDeliveryAfterDiscount}
          priceDeliveryBeforeDiscount={priceDeliveryBeforeDiscount}
          priceDeliveryDiscount={priceDeliveryDiscount}
          priceDiscount={priceDiscount}
          priceDishesAfterDiscount={priceDishesAfterDiscount}
          priceDishesBeforeDiscount={priceDishesBeforeDiscount}
          priceDishesDiscount={priceDishesDiscount}
        />
      </div>
    ),
    sortable: false,
    filterable: false,
    width: 100,
  },
  {
    title: 'ecommerceBags.fields.income',
    accessor: row => row.income.toFixed(2),
    name: 'income',
    width: 70,
    filterable: false,
    sortable: false,
  },

  {
    title: 'ecommerceBags.fields.bag.diet.name',
    accessor: 'diet.name',
    name: 'diet.name',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.diet.name',
    accessor: 'clientDiet.diet.name',
    name: 'clientDiet.diet.name',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.variant.name',
    accessor: 'variant.name',
    name: 'variant.name',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.variant.name',
    accessor: 'clientDiet.variant.name',
    name: 'clientDiet.variant.name',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.calorific.name',
    accessor: 'calorific.name',
    name: 'calorific.name',
    filterable: true,
    sortable: true,
  },

  {
    title: 'ecommerceBags.fields.bag.clientDiet.calorific.name',
    accessor: 'clientDiet.calorific.name',
    name: 'clientDiet.calorific.name',
    filterable: true,
    sortable: true,
  },
  {
    title: 'ecommerceBags.fields.bag.optionChangeMenu',
    accessor: row => <BooleanView value={row?.optionChangeMenu} />,
    name: 'optionChangeMenu',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.optionChangeMenu',
    accessor: row => <BooleanView value={row?.clientDiet?.optionChangeMenu} />,
    name: 'clientDiet.optionChangeMenu',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.useEcoContainers',
    accessor: row => <BooleanView value={row?.useEcoContainers} />,
    name: 'useEcoContainers',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.bag.clientDiet.useEcoContainers',
    accessor: row => <BooleanView value={row?.clientDiet?.useEcoContainers} />,
    name: 'clientDiet.useEcoContainers',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: true,
    sortable: false,
  },
  {
    title: 'ecommerceBags.fields.clientChanged',
    accessor: row => <BooleanView value={row?.clientChanged} />,
    name: 'clientChanged',
    width: 70,
    Filter: YesNoDataGridFilter,
    filterable: false,
    sortable: false,
  },
];

export default columnConfig;
